import { useMemo, useCallback } from 'react';

import { useApp } from '../../../app/AppContext.jsx';
import { useCatalog } from '../../catalog/context/CatalogContext.jsx';

import { isProductAccessible } from '../../catalog/utils/unitTableSettings.js';
import { parsePricesVisibility } from '../../../shared/utils/index.js'
import { SECTION_ID } from '../../../shared/constants/sectionIds.js'

import { useFavoritesFiltered } from '../hooks/useFavoritesFiltered.js';
import { useFavoritesPinning } from '../hooks/useFavoritesPinning.js';
import { useFavoritesScrolling } from '../hooks/useFavoritesScrolling.js';

import { FavoritesCompareBlock } from './FavoritesCompareBlock.jsx';
import { FavoritesListBlock } from './FavoritesListBlock.jsx';
import { FavoritesPlaceholder } from './FavoritesPlaceholder.jsx';
import { FavoritesTopBar } from './FavoritesTopBar.jsx';

import { Logger } from '../../../shared/utils/logger.js'

import styles from './FavoritesView.module.scss';

export function FavoritesView() {
	// ── Context reads ─────────────────────────────────────────────────────────
	const {
		appCapabilities,
		activateSectionHandler,
		clearFavorites,
		favoriteUuids,
		favoritesCount,
		logoSrc,
    panelSettings,
		sectionRefs,
		selectProductHandler,
		toggleFavorite,
		windowData,
		isMobile,
	} = useApp();

	const { products, filteredProducts, compartments } = useCatalog();


	// ── Domain hooks ──────────────────────────────────────────────────────────
	const {
		allFavoriteProducts,
		areSomeFavoritesHidden,
		visibleFavorites,
		hiddenFavoritesCount,
	} = useFavoritesFiltered({
		favoriteUuids,
		filteredProducts,
		allProducts: products,
	});

	const { pinnedUuids, isPinned, canPin, togglePin, unpinAll } =
		useFavoritesPinning({ favoriteUuids });

	const { wireScrollListeners } = useFavoritesScrolling({ favoritesCount });

	// ── Derived state ─────────────────────────────────────────────────────────
	const {
		reservedUnitsAccessible,
		reservedUnitsPlanVisible, 
		soldUnitsAccessible,      
		soldUnitsPlanVisible,
	} = appCapabilities;

	const pinnedProducts = useMemo(
		() =>
			pinnedUuids
				.map((uuid) => allFavoriteProducts.find((product) => product.uuid === uuid))
				.filter(Boolean),
		[allFavoriteProducts, pinnedUuids],
	);

  const projectDataUrl = windowData?.projectDataUrl ?? null;
  const pricesVisibility = parsePricesVisibility(panelSettings);

	// ── Callbacks ─────────────────────────────────────────────────────────────
	const handleDiscoverProducts = useCallback(() => {
		activateSectionHandler?.(SECTION_ID.CATALOG, {
			scrollTarget: SECTION_ID.CATALOG,
		});
	}, [activateSectionHandler]);

	const handleClearAll = useCallback(() => {
		clearFavorites?.();
		unpinAll?.();
	}, [clearFavorites, unpinAll]);

	const handleProductClick = useCallback(
		(product) => {
			if (!product?.uuid) return;
			const isAccessible = isProductAccessible(product, {reservedUnitsAccessible, soldUnitsAccessible});
			if (!isAccessible) return;

			selectProductHandler?.(product.uuid, {
				buildingId: product.building_id ?? null,
				refTier: product.ref_tier ?? null,
			});
		},
		[selectProductHandler],
	);

	const handleToggleFavorite = useCallback(
		(product) => {
			if (!product?.uuid) return;
			toggleFavorite?.(product.uuid);
		},
		[toggleFavorite],
	);

	const handleTogglePin = useCallback(
		(product) => {
			if (!product?.uuid) return;
			togglePin(product.uuid);
		},
		[togglePin],
	);

	const allFavoritesEventDetails = useMemo(
		() => 
			 allFavoriteProducts.map((product) => ({
      investmentId: product?.crm_id_investment ?? null,
      stageId: product?.crm_id_stage ?? null,
      buildingId: product?.crm_id_building ?? null,
      unitId: product?.crm_id_unit ?? product?.number ?? product?.id_cad ?? null,
      unitName: product?.id_original ?? product.cms_product_name ?? product?.id_cad ?? null,
      userAddedItems: null
		})),
		[allFavoriteProducts]
	);

	const handleInquireAllFavorites = useCallback(() => {
		const inquireAllEventDetail = {
			units: allFavoritesEventDetails
		};

		Logger.info({
			message: 'FavoritesView cadaplusProductQueryClick event for all favorites dispatched',
			content: JSON.stringify(inquireAllEventDetail)
		});

		const event = new CustomEvent('cadaplusProductQueryClick', {
			detail: inquireAllEventDetail,
			bubbles: true,
			composed: true,
		});

		document.dispatchEvent(event);
	}, [allFavoritesEventDetails]);

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<section ref={sectionRefs[SECTION_ID.FAVORITES]} className={styles.favoritesView}>
			
			<div className={styles.canvas}>
				<FavoritesTopBar
					favoritesCount={favoritesCount}
					hiddenFavoritesCount={hiddenFavoritesCount}
					areSomeFavoritesHidden={areSomeFavoritesHidden}
					onClearAll={handleClearAll}
					onInquireAll={handleInquireAllFavorites}
				/>

        {allFavoriteProducts.length === 0 ? (
          <FavoritesPlaceholder onDiscoverProducts={handleDiscoverProducts} />
        ) : (
          <FavoritesListBlock
						isMobile={isMobile}
            products={visibleFavorites}
            pinnedProducts={pinnedProducts}
            panelSettings={panelSettings}
            isPinned={isPinned}
            canPin={canPin}
            wireScrollListeners={wireScrollListeners}
            projectDataUrl={projectDataUrl}
            logoSrc={logoSrc ?? null}
            onProductClick={handleProductClick}
            onToggleFavorite={handleToggleFavorite}
						onTogglePin={handleTogglePin}
						reservedUnitsAccessible={reservedUnitsAccessible}
						reservedUnitsPlanVisible={reservedUnitsPlanVisible}
						soldUnitsAccessible={soldUnitsAccessible}
						soldUnitsPlanVisible={soldUnitsPlanVisible}
          />
        )}
          <FavoritesCompareBlock
						isMobile={isMobile}
            projectDataUrl={projectDataUrl}
            pinnedProducts={pinnedProducts}
            compareRows={panelSettings?.favorites_comparison?.rows ?? []}
            compartmentTypes={compartments}
            logoSrc={logoSrc}
            // use3dThumbs={use3dThumbs} // TODO: finalize panelSettings key (e.g. panelSettings?.unit_thumb_3d === true)
            pricesVisibility={pricesVisibility}
            onTogglePin={(uuid) => handleTogglePin?.({ uuid })}
            onOpenProduct={handleProductClick}
						reservedUnitsAccessible={reservedUnitsAccessible}
						reservedUnitsPlanVisible={reservedUnitsPlanVisible}
						soldUnitsAccessible={soldUnitsAccessible}
						soldUnitsPlanVisible={soldUnitsPlanVisible}
          />
			</div>
		</section>
	);
}
